import React, { useState, useEffect, useRef } from 'react';

const CourtPage = ({ courtNumber = 2 }) => {
    const [match, setMatch] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [error, setError] = useState(null);
    const pollingIntervalRef = useRef(null);
    const sideLabelsCache = useRef(null);

    const API_BASE_URL = 'https://api.asterickx.com/api/MatchScoring';
    const API_ALL_LIVE = `${API_BASE_URL}/GetAllLiveScore`;
    const TOURNAMENT_ID = '7f479cd9-9169-488b-8f06-dc57b6ab64b1';
    const API_SIDE_LABELS = `${API_BASE_URL}/tournament/${TOURNAMENT_ID}/side-labels`;

    const fetchSideLabels = async () => {
        if (sideLabelsCache.current) {
            return sideLabelsCache.current;
        }

        try {
            const response = await fetch(API_SIDE_LABELS);
            if (!response.ok) {
                return null;
            }
            const data = await response.json();
            sideLabelsCache.current = data;
            return data;
        } catch (error) {
            console.error('Error fetching side labels:', error);
            return null;
        }
    };

    const parseSideDisplay = (displayString) => {
        if (!displayString) return { players: [], state: '' };
        
        // Extract state from parentheses at the end
        const stateMatch = displayString.match(/\(([^)]+)\)\s*$/);
        const state = stateMatch ? stateMatch[1] : '';
        
        // Remove state part and extract player names
        const namesOnly = displayString.replace(/\s*\([^)]+\)\s*$/, '').trim();
        
        // Split by & or / to get individual players
        let players = [];
        if (namesOnly.includes(' & ')) {
            players = namesOnly.split(' & ').map(p => p.trim());
        } else if (namesOnly.includes(' / ')) {
            players = namesOnly.split(' / ').map(p => p.trim());
        } else {
            players = [namesOnly];
        }
        
        return { players, state };
    };

    const getMemberDetailsFromSideLabels = (sideLabels, matchId) => {
        if (!sideLabels || !Array.isArray(sideLabels)) return null;
        
        const matchData = sideLabels.find(item => item.matchId === matchId);
        if (!matchData) return null;

        // Parse side1Display and side2Display
        const side1Parts = parseSideDisplay(matchData.side1Display);
        const side2Parts = parseSideDisplay(matchData.side2Display);

        return {
            side1: {
                label: matchData.side1Display || '',
                state: side1Parts.state,
                players: {
                    p1A: side1Parts.players[0] || '',
                    p1B: side1Parts.players[1] || ''
                }
            },
            side2: {
                label: matchData.side2Display || '',
                state: side2Parts.state,
                players: {
                    p2A: side2Parts.players[0] || '',
                    p2B: side2Parts.players[1] || ''
                }
            }
        };
    };

    const fetchMatchDetails = async (matchId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/${matchId}`);
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error(`Error fetching match ${matchId}:`, error);
            return null;
        }
    };

    const getFirstName = (fullName) => {
        if (!fullName) return '';
        return fullName.trim().split(' ')[0];
    };

    const parsePlayerNames = (nameString) => {
        if (!nameString) return [];
        
        let players = [];
        
        if (nameString.includes(' & ')) {
            players = nameString.split(' & ').map(p => getFirstName(p));
        } else if (nameString.includes(' / ')) {
            players = nameString.split(' / ').map(p => getFirstName(p));
        } else if (nameString.includes('/')) {
            players = nameString.split('/').map(p => getFirstName(p));
        } else {
            players = [getFirstName(nameString)];
        }
        
        return players.filter(p => p.length > 0);
    };

    const convertApiDataToMatch = async (apiData, liveData, sideLabelsData = null) => {
        const matchData = apiData || liveData;
        const matchId = matchData.matchId;

        // Get side label data for this match
        const matchSideData = sideLabelsData ? getMemberDetailsFromSideLabels(sideLabelsData, matchId) : null;

        const sets = matchData.sets || [];

        let currentSetNumber = 1;
        if (liveData?.currentSetNumber) {
            currentSetNumber = liveData.currentSetNumber;
        } else {
            for (let i = 0; i < sets.length; i++) {
                if (sets[i].side1Score > 0 || sets[i].side2Score > 0) {
                    currentSetNumber = i + 1;
                }
            }
        }

        let p1A = "", p1B = "", p2A = "", p2B = "";
        let side1State = "", side2State = "";
        
        // Use side labels data if available
        if (matchSideData) {
            p1A = matchSideData.side1.players.p1A || "";
            p1B = matchSideData.side1.players.p1B || "";
            p2A = matchSideData.side2.players.p2A || "";
            p2B = matchSideData.side2.players.p2B || "";
            side1State = matchSideData.side1.state || "";
            side2State = matchSideData.side2.state || "";
        }

        const isDoubles = matchData.categoryName?.toLowerCase().includes('doubles') || 
                          (p1B && p1B.trim() !== "") || (p2B && p2B.trim() !== "");
        
        const side1Name = matchData.side1Label || 'Player 1';
        const side2Name = matchData.side2Label || 'Player 2';

        let side1Players = [];
        let side2Players = [];

        if (p1A && p1A.trim() !== "") {
            side1Players.push(getFirstName(p1A));
        }
        if (p1B && p1B.trim() !== "") {
            side1Players.push(getFirstName(p1B));
        }
        if (p2A && p2A.trim() !== "") {
            side2Players.push(getFirstName(p2A));
        }
        if (p2B && p2B.trim() !== "") {
            side2Players.push(getFirstName(p2B));
        }

        if (side1Players.length === 0) {
            side1Players = parsePlayerNames(side1Name);
        }
        if (side2Players.length === 0) {
            side2Players = parsePlayerNames(side2Name);
        }

        const side1Scores = [0, 0, 0];
        const side2Scores = [0, 0, 0];

        sets.forEach((set, index) => {
            if (index < 3) {
                side1Scores[index] = set.side1Score || 0;
                side2Scores[index] = set.side2Score || 0;
            }
        });

        if (isDoubles || side1Players.length > 1 || side2Players.length > 1) {
            return {
                id: matchData.matchId || liveData.matchId,
                categoryName: matchData.categoryName,
                roundName: matchData.roundName,
                team1: {
                    players: side1Players,
                    state: side1State,
                    scores: side1Scores,
                    serving: matchData.currentServerSide === 1
                },
                team2: {
                    players: side2Players,
                    state: side2State,
                    scores: side2Scores,
                    serving: matchData.currentServerSide === 2
                },
                currentSet: currentSetNumber,
                isDoubles: true
            };
        } else {
            return {
                id: matchData.matchId || liveData.matchId,
                categoryName: matchData.categoryName,
                roundName: matchData.roundName,
                player1: {
                    name: side1Players[0] || side1Name,
                    state: side1State,
                    scores: side1Scores,
                    serving: matchData.currentServerSide === 1
                },
                player2: {
                    name: side2Players[0] || side2Name,
                    state: side2State,
                    scores: side2Scores,
                    serving: matchData.currentServerSide === 2
                },
                currentSet: currentSetNumber,
                isDoubles: false
            };
        }
    };

    const fetchCourtMatch = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            setError(null);

            // Fetch side labels data
            const sideLabelsData = await fetchSideLabels();

            const response = await fetch(API_ALL_LIVE);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const allMatchesData = await response.json();

            if (!Array.isArray(allMatchesData) || allMatchesData.length === 0) {
                setMatch(null);
                if (!silent) setLoading(false);
                setLastUpdate(new Date().toLocaleTimeString());
                return;
            }

            const courtNumberStr = String(courtNumber);
            
            const courtMatch = allMatchesData.find(
                m => m.matchStatus === "In Progress" && m.courtNumber === courtNumberStr
            );

            if (courtMatch) {
                const details = await fetchMatchDetails(courtMatch.matchId);
                const convertedMatch = await convertApiDataToMatch(details, courtMatch, sideLabelsData);
                setMatch(convertedMatch);
            } else {
                setMatch(null);
            }

            setLastUpdate(new Date().toLocaleTimeString());
            if (!silent) setLoading(false);
        } catch (error) {
            console.error('Error fetching match:', error);
            setError(error.message);
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        fetchCourtMatch();
        pollingIntervalRef.current = setInterval(() => {
            fetchCourtMatch(true);
        }, 5000);

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, [courtNumber]);

    const getSetWinner = (setIndex) => {
        if (!match) return null;
        const p1Score = match.isDoubles ? match.team1.scores[setIndex] : match.player1.scores[setIndex];
        const p2Score = match.isDoubles ? match.team2.scores[setIndex] : match.player2.scores[setIndex];

        if (p1Score === undefined || p2Score === undefined) return null;
        if (p1Score >= 21 && p1Score - p2Score >= 2) return 1;
        if (p2Score >= 21 && p2Score - p1Score >= 2) return 2;
        if (p1Score >= 30 || p2Score >= 30) return p1Score > p2Score ? 1 : 2;
        return null;
    };

    const getSetsWon = (side) => {
        if (!match) return 0;
        let won = 0;
        for (let i = 0; i < 3; i++) {
            if (getSetWinner(i) === side) won++;
        }
        return won;
    };

    if (error) {
        return (
            <div style={{ padding: '40px', textAlign: 'center', background: '#fee', color: '#c33' }}>
                <strong>Error:</strong> {error}
            </div>
        );
    }

    if (!match) {
        return (
            <div style={{
                minHeight: '100vh',
                background: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
            }}>
                <div style={{
                    textAlign: 'center',
                    background: '#f5f5f5',
                    padding: '60px',
                    borderRadius: '12px',
                    border: '2px solid #e0e0e0'
                }}>
                    <div style={{ fontSize: '80px', marginBottom: '20px' }}>🏸</div>
                    <h2 style={{ color: '#333', marginBottom: '10px', fontSize: '28px' }}>Court {courtNumber}</h2>
                    <p style={{ color: '#666', fontSize: '18px' }}>
                        {loading && !lastUpdate ? 'Loading...' : 'No Live Match'}
                    </p>
                </div>
            </div>
        );
    }

    const side1 = match.isDoubles ? match.team1 : match.player1;
    const side2 = match.isDoubles ? match.team2 : match.player2;
    const side1Name = match.isDoubles ? side1.players.join(' & ') : side1.name;
    const side2Name = match.isDoubles ? side2.players.join(' & ') : side2.name;

    return (
        <div style={{
            minHeight: '100vh',
            background: '#ffffff',
            padding: '30px',
            fontFamily: 'Arial, sans-serif'
        }}>
            <div style={{
                width: 'fit-content',
                maxWidth: '700px'
            }}>
                <div style={{
                    background: '#f5f5f5',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    border: '2px solid #e0e0e0'
                }}>
                    {/* Player 1 Row */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '50px 280px repeat(3, 50px)',
                        alignItems: 'stretch',
                        borderBottom: '2px solid #d0d0d0'
                    }}>
                        {/* Icon */}
                        <div style={{
                            background: '#e8e8e8',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '10px'
                        }}>
                            <div style={{ fontSize: '24px' }}>🏸</div>
                        </div>

                        {/* Name and State */}
                        <div style={{
                            padding: '10px 15px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            background: '#ffffff',
                            gap: '2px',
                            borderBottom: '1px solid #e0e0e0'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    color: '#1a1a1a',
                                    textTransform: 'uppercase',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {side1Name}
                                </span>
                                {side1.serving && (
                                    <div style={{
                                        background: '#ffd700',
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        flexShrink: 0
                                    }} />
                                )}
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: '#666',
                                fontWeight: '500'
                            }}>
                                {side1.state || ''}
                            </div>
                        </div>

                        {/* Set Scores */}
                        {[0, 1, 2].map((setIndex) => {
                            const score = side1.scores[setIndex];
                            const isCurrentSet = match.currentSet === setIndex + 1;
                            const winner = getSetWinner(setIndex);

                            return (
                                <div
                                    key={setIndex}
                                    style={{
                                        background: isCurrentSet ? '#ffd700' : winner === 1 ? '#4caf50' : '#e8e8e8',
                                        color: isCurrentSet || winner === 1 ? (isCurrentSet ? '#000' : '#fff') : '#666',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '22px',
                                        fontWeight: 'bold',
                                        padding: '10px 0',
                                        borderBottom: '1px solid #d0d0d0'
                                    }}
                                >
                                    {score > 0 ? score : '-'}
                                </div>
                            );
                        })}
                    </div>

                    {/* Player 2 Row */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '50px 280px repeat(3, 50px)',
                        alignItems: 'stretch'
                    }}>
                        {/* Icon */}
                        <div style={{
                            background: '#e8e8e8',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '10px'
                        }}>
                            <div style={{ fontSize: '24px' }}>🏸</div>
                        </div>

                        {/* Name and State */}
                        <div style={{
                            padding: '10px 15px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            background: '#ffffff',
                            gap: '2px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    color: '#1a1a1a',
                                    textTransform: 'uppercase',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {side2Name}
                                </span>
                                {side2.serving && (
                                    <div style={{
                                        background: '#ffd700',
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        flexShrink: 0
                                    }} />
                                )}
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: '#666',
                                fontWeight: '500'
                            }}>
                                {side2.state || ''}
                            </div>
                        </div>

                        {/* Set Scores */}
                        {[0, 1, 2].map((setIndex) => {
                            const score = side2.scores[setIndex];
                            const isCurrentSet = match.currentSet === setIndex + 1;
                            const winner = getSetWinner(setIndex);

                            return (
                                <div
                                    key={setIndex}
                                    style={{
                                        background: isCurrentSet ? '#ffd700' : winner === 2 ? '#4caf50' : '#e8e8e8',
                                        color: isCurrentSet || winner === 2 ? (isCurrentSet ? '#000' : '#fff') : '#666',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '22px',
                                        fontWeight: 'bold',
                                        padding: '10px 0'
                                    }}
                                >
                                    {score > 0 ? score : '-'}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Match Info Below */}
                <div style={{
                    marginTop: '15px',
                    padding: '10px 15px',
                    background: '#f5f5f5',
                    borderRadius: '6px',
                    color: '#666',
                    fontSize: '14px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid #e0e0e0'
                }}>
                    <div>
                        <strong style={{ color: '#1a1a1a' }}>Court {courtNumber}</strong> • {match.categoryName} • {match.roundName}
                    </div>
                    <div>
                        Set {match.currentSet} • {getSetsWon(1)}-{getSetsWon(2)}
                        {lastUpdate && <span style={{ marginLeft: '10px' }}>• {lastUpdate}</span>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default () => <CourtPage courtNumber={2} />;